import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { UsageCharts, type StatsRow } from '@/components/Charts';
import { PageHeader } from '@/components/Layout';
import { Button, Card, ErrorNote, Loading, Stat } from '@/components/ui';
import { api } from '@/lib/api';
import { fmtInt, fmtMs, fmtUsd } from '@/lib/format';
import { rk } from '@/lib/hooks';

const RANGES = [
  { hours: 24, label: '24h' },
  { hours: 24 * 7, label: '7d' },
  { hours: 24 * 30, label: '30d' },
  { hours: 24 * 90, label: '90d' },
];

type Row = { key: string; label: string; requests: number; errors: number; costUsd: number | null; inputTokens: number; outputTokens: number; p95Ms: number | null };

const Breakdown = ({ by, hours, title, note }: { by: 'model' | 'agent' | 'project'; hours: number; title: string; note: string }) => {
  const q = useQuery({ queryKey: rk('breakdown', by, hours), queryFn: () => api<{ rows: Row[] }>(`/v1/stats/breakdown?by=${by}&hours=${hours}`) });
  const rows = q.data?.rows ?? [];
  const total = rows.reduce((a, r) => a + (r.costUsd ?? 0), 0);
  return (
    <Card title={title}>
      <p className="px-4 pt-3 text-xs text-ink-3">{note}</p>
      {q.isLoading ? (
        <Loading />
      ) : q.error ? (
        <div className="p-4">
          <ErrorNote error={q.error} />
        </div>
      ) : !rows.length ? (
        <p className="px-4 py-3 text-[13px] text-ink-3">No production traffic in this period.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="text-left text-xs text-ink-3">
              <tr>
                <th className="px-4 py-2 font-medium">{by === 'model' ? 'Model' : by === 'agent' ? 'Agent' : 'Product'}</th>
                <th className="px-2 py-2 text-right font-medium">Calls</th>
                <th className="px-2 py-2 text-right font-medium">Failed</th>
                <th className="px-2 py-2 text-right font-medium">Tokens in / out</th>
                <th className="px-2 py-2 text-right font-medium">p95</th>
                <th className="px-2 py-2 text-right font-medium">Cost</th>
                <th className="px-4 py-2 text-right font-medium">Share</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-t border-line-2">
                  <td className="px-4 py-2 font-medium">{r.label}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{fmtInt(r.requests)}</td>
                  <td className={`px-2 py-2 text-right tabular-nums ${r.errors ? 'text-bad' : ''}`}>{fmtInt(r.errors)}</td>
                  <td className="px-2 py-2 text-right tabular-nums">
                    {fmtInt(r.inputTokens)} / {fmtInt(r.outputTokens)}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums">{fmtMs(r.p95Ms)}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{fmtUsd(r.costUsd)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{total > 0 && r.costUsd !== null ? `${Math.round((r.costUsd / total) * 100)}%` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

/** Control board: where requests, tokens and money go — by model, agent and product. */
export const Usage = () => {
  const [hours, setHours] = useState(24 * 7);
  const stats = useQuery({ queryKey: rk('stats', 'all', hours), queryFn: () => api<{ bucket: 'hour' | 'day'; series: StatsRow[] }>(`/v1/stats?hours=${hours}`) });
  const s = stats.data?.series ?? [];
  const sum = (k: 'requests' | 'errors') => s.reduce((a, r) => a + r[k], 0);
  const cost = s.reduce((a, r) => a + (r.costUsd ?? 0), 0);
  return (
    <>
      <PageHeader
        title="Usage"
        subtitle="Production traffic by model, agent and product. Compare models on an agent’s own test cases in its Compare models tab."
        actions={
          <div className="flex gap-1">
            {RANGES.map((r) => (
              <Button key={r.hours} size="sm" variant={r.hours === hours ? 'primary' : 'ghost'} onClick={() => setHours(r.hours)}>
                {r.label}
              </Button>
            ))}
          </div>
        }
      />
      <div className="mb-4 grid grid-cols-2 gap-5 rounded-lg border border-line bg-panel p-4 sm:grid-cols-4">
        <Stat label="Requests" value={fmtInt(sum('requests'))} />
        <Stat label="Failed" value={fmtInt(sum('errors'))} tone={sum('errors') ? 'bad' : undefined} />
        <Stat label="Cost" value={fmtUsd(cost)} />
        <Stat label="Cost per request" value={sum('requests') ? fmtUsd(cost / sum('requests')) : '—'} />
      </div>
      {stats.data && <UsageCharts className="mb-5" rows={stats.data.series} hours={hours} bucket={stats.data.bucket} />}
      <div className="flex flex-col gap-5">
        <Breakdown by="model" hours={hours} title="By model" note="Every call to a model, including each step of a flow and each tool-calling turn." />
        <Breakdown by="agent" hours={hours} title="By agent" note="Whole requests per agent." />
        <Breakdown by="project" hours={hours} title="By product" note="Apply with Bloom vs the students platform." />
      </div>
    </>
  );
};
